/**
 * Card representation.
 *
 * A Guandan deck is two standard 54-card decks: 108 cards total. Every card is
 * packed into a single small integer so hands can be sorted, compared and
 * histogrammed without allocating objects:
 *
 *     card = (rank << 3) | (suit << 1) | deck
 *
 * Ranks run 2..14 (A = 14) for the pip cards, with 15 and 16 reserved for the
 * small and big joker. `deck` distinguishes the two identical copies of a card,
 * which matters because a play must name specific physical cards.
 */

export type Card = number;
export type Rank = number;
export type Suit = 0 | 1 | 2 | 3;

export const DIAMOND = 0 as const;
export const CLUB = 1 as const;
export const HEART = 2 as const;
export const SPADE = 3 as const;

export const RANK_2 = 2;
export const RANK_10 = 10;
export const RANK_J = 11;
export const RANK_Q = 12;
export const RANK_K = 13;
export const RANK_A = 14;
export const SMALL_JOKER = 15;
export const BIG_JOKER = 16;

/** Lowest and highest rank that can appear inside a straight-like combo. */
export const MIN_PIP = RANK_2;
export const MAX_PIP = RANK_A;

export function makeCard(rank: Rank, suit: Suit, deck: 0 | 1): Card {
  return (rank << 3) | (suit << 1) | deck;
}

export function rankOf(card: Card): Rank {
  return card >> 3;
}

export function suitOf(card: Card): Suit {
  return ((card >> 1) & 3) as Suit;
}

export function deckOf(card: Card): 0 | 1 {
  return (card & 1) as 0 | 1;
}

export function isJoker(card: Card): boolean {
  return card >> 3 >= SMALL_JOKER;
}

export function isPip(card: Card): boolean {
  return card >> 3 <= MAX_PIP;
}

/**
 * The heart of the level rank is the wildcard (逢人配). Both copies can stand in
 * for any non-joker card when forming a combo. Played alone it is simply a level
 * card, so wildness is a property of how a card is *used*, not of the card.
 */
export function isWildcard(card: Card, level: Rank): boolean {
  return rankOf(card) === level && suitOf(card) === HEART && level <= MAX_PIP;
}

/**
 * Ordering of single cards. The level rank is promoted to sit above the ace but
 * below the jokers; everything else keeps its natural order. Returned values are
 * only meaningful relative to each other.
 */
export function singleOrder(rank: Rank, level: Rank): number {
  if (rank >= SMALL_JOKER) return rank + 10; // 25, 26
  if (rank === level) return 20; // above A (14), below the jokers
  return rank;
}

/** Build a fresh, ordered 108-card deck. */
export function buildDeck(): Card[] {
  const cards: Card[] = [];
  for (let rank = MIN_PIP; rank <= MAX_PIP; rank++) {
    for (let suit = 0 as Suit; suit <= 3; suit = (suit + 1) as Suit) {
      cards.push(makeCard(rank, suit, 0));
      cards.push(makeCard(rank, suit, 1));
    }
  }
  for (const rank of [SMALL_JOKER, BIG_JOKER]) {
    cards.push(makeCard(rank, DIAMOND, 0));
    cards.push(makeCard(rank, DIAMOND, 1));
  }
  return cards;
}

const RANK_LABELS: Record<number, string> = {
  2: "2", 3: "3", 4: "4", 5: "5", 6: "6", 7: "7", 8: "8", 9: "9",
  10: "10", 11: "J", 12: "Q", 13: "K", 14: "A",
};

const SUIT_LABELS = ["♦", "♣", "♥", "♠"] as const;

export function rankLabel(rank: Rank): string {
  if (rank === SMALL_JOKER) return "sJ";
  if (rank === BIG_JOKER) return "BJ";
  return RANK_LABELS[rank] ?? `?${rank}`;
}

export function cardLabel(card: Card): string {
  const rank = rankOf(card);
  if (rank >= SMALL_JOKER) return rankLabel(rank);
  return `${SUIT_LABELS[suitOf(card)]}${rankLabel(rank)}`;
}

export function handLabel(cards: readonly Card[]): string {
  return cards.map(cardLabel).join(" ");
}

/** Sort a hand for display: by promoted single order, then suit. */
export function sortHand(cards: Card[], level: Rank): Card[] {
  return cards.sort((a, b) => {
    const d = singleOrder(rankOf(a), level) - singleOrder(rankOf(b), level);
    if (d !== 0) return d;
    return suitOf(a) - suitOf(b);
  });
}
